import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { dotwApi, formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Send, CheckCircle, ImagePlus, X, Loader2 } from 'lucide-react';

const QA_TEMPLATE = [
  'Name, hometown, and track(s) raced',
  'How did you get started in Crown Vic racing?',
  'Why did you choose Crown Vic racing?',
  'Racing experience and biggest highlight',
  'Car nickname or setup details',
  'Toughest track and why',
  'People who helped your racing journey',
  'Fun fact fans might not know',
  'Goals for the season',
  'Shoutouts to sponsors, crew, or supporters',
];

const MAX_PHOTOS = 3;

export default function SubmitDriverDialog() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [photos, setPhotos] = useState([]);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    driver_name: '',
    hometown: '',
    car_number: '',
    track: '',
    qa: QA_TEMPLATE.map((q) => ({ question: q, answer: '' })),
  });

  const updateQA = (i, val) => {
    setForm((f) => ({
      ...f,
      qa: f.qa.map((q, idx) => (idx === i ? { ...q, answer: val } : q)),
    }));
  };

  const addPhotos = (e) => {
    const files = Array.from(e.target.files || []);
    setPhotos((prev) => [...prev, ...files].slice(0, MAX_PHOTOS));
    e.target.value = '';
  };

  const removePhoto = (i) => setPhotos((p) => p.filter((_, idx) => idx !== i));

  const reset = () => {
    setForm({
      driver_name: '',
      hometown: '',
      car_number: '',
      track: '',
      qa: QA_TEMPLATE.map((q) => ({ question: q, answer: '' })),
    });
    setPhotos([]);
    setError('');
    setSuccess(false);
    setUploadProgress(null);
  };

  const handleTrigger = (e) => {
    if (!user) {
      e.preventDefault();
      navigate('/login', { state: { from: { pathname: '/driver-of-the-week' } } });
      return;
    }
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.driver_name.trim()) {
      setError('Driver name is required.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const res = await dotwApi.submit(form);
      const driverId = res.data?.id;
      if (driverId && photos.length > 0) {
        setUploadProgress({ done: 0, total: photos.length });
        for (let i = 0; i < photos.length; i += 1) {
          try {
            await dotwApi.uploadImage(driverId, photos[i]);
          } catch (imgErr) {
            console.error('Photo upload failed', imgErr);
          }
          setUploadProgress({ done: i + 1, total: photos.length });
        }
      }
      setSuccess(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button
          onClick={handleTrigger}
          className="bg-[#FF3B30] hover:bg-[#E6352B] text-white"
          data-testid="submit-entry-btn"
        >
          <Send className="w-4 h-4 mr-2" />
          Submit Your Entry
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Submit a Driver of the Week Entry</DialogTitle>
        </DialogHeader>

        {success ? (
          <div className="py-10 text-center">
            <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-4" />
            <h3 className="text-white font-heading font-bold text-xl mb-2">Entry Submitted!</h3>
            <p className="text-gray-400 text-sm max-w-md mx-auto">
              Thanks for your submission. An admin will review your entry and publish it to the
              Driver of the Week page. You'll get a shoutout when it goes live.
            </p>
            <Button
              onClick={() => {
                setOpen(false);
                reset();
              }}
              className="mt-6 bg-[#FF3B30] hover:bg-[#E6352B]"
              data-testid="submit-entry-done-btn"
            >
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <p className="text-gray-400 text-sm">
              Answer the questions below and upload up to {MAX_PHOTOS} photos. Your entry will be
              reviewed by an admin before being published.
            </p>

            {error && (
              <div className="bg-red-500/10 border border-red-500/40 text-red-400 px-3 py-2 rounded text-sm">
                {error}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Driver Name *</Label>
                <Input
                  value={form.driver_name}
                  onChange={(e) => setForm({ ...form, driver_name: e.target.value })}
                  placeholder="John Smith"
                  className="bg-[#1F1F1F] border-[#262626] text-white"
                  required
                  data-testid="submit-name-input"
                />
              </div>
              <div>
                <Label>Hometown</Label>
                <Input
                  value={form.hometown}
                  onChange={(e) => setForm({ ...form, hometown: e.target.value })}
                  placeholder="Dayton, OH"
                  className="bg-[#1F1F1F] border-[#262626] text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Car Number</Label>
                <Input
                  value={form.car_number}
                  onChange={(e) => setForm({ ...form, car_number: e.target.value })}
                  placeholder="17"
                  className="bg-[#1F1F1F] border-[#262626] text-white"
                />
              </div>
              <div>
                <Label>Home Track</Label>
                <Input
                  value={form.track}
                  onChange={(e) => setForm({ ...form, track: e.target.value })}
                  placeholder="Shadybowl Speedway"
                  className="bg-[#1F1F1F] border-[#262626] text-white"
                />
              </div>
            </div>

            <div>
              <Label>Photos (up to {MAX_PHOTOS})</Label>
              <div className="mt-2 grid grid-cols-3 gap-2" data-testid="submit-photo-grid">
                {photos.map((f, idx) => (
                  <div
                    key={idx}
                    className="relative group aspect-square bg-[#1F1F1F] border border-[#262626] rounded overflow-hidden"
                  >
                    <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removePhoto(idx)}
                      className="absolute top-1 right-1 w-6 h-6 bg-black/70 hover:bg-red-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label={`Remove photo ${idx + 1}`}
                    >
                      <X className="w-3.5 h-3.5 text-white" />
                    </button>
                  </div>
                ))}
                {photos.length < MAX_PHOTOS && (
                  <label className="aspect-square border-2 border-dashed border-[#262626] hover:border-[#FF3B30] rounded flex flex-col items-center justify-center text-gray-500 hover:text-[#FF3B30] cursor-pointer transition-colors">
                    <ImagePlus className="w-6 h-6 mb-1" />
                    <span className="text-[10px] uppercase tracking-wider">
                      {photos.length === 0 ? 'Add photos' : 'Add more'}
                    </span>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={addPhotos}
                      className="hidden"
                      data-testid="submit-photo-input"
                    />
                  </label>
                )}
              </div>
              <p className="text-gray-500 text-xs mt-1">
                {photos.length}/{MAX_PHOTOS} selected
              </p>
            </div>

            <div className="border-t border-[#262626] pt-5">
              <h3 className="font-heading font-bold text-lg text-white mb-4">Driver Q&amp;A</h3>
              <div className="space-y-4">
                {form.qa.map((item, i) => (
                  <div key={i}>
                    <Label className="text-[#FF3B30] text-xs uppercase tracking-wider">
                      {item.question}
                    </Label>
                    <Textarea
                      value={item.answer}
                      onChange={(e) => updateQA(i, e.target.value)}
                      placeholder="Your answer..."
                      rows={2}
                      className="bg-[#1F1F1F] border-[#262626] text-white mt-1"
                      data-testid={`submit-qa-${i}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#FF3B30] hover:bg-[#E6352B]"
              data-testid="submit-entry-send-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {uploadProgress
                    ? `Uploading photos ${uploadProgress.done}/${uploadProgress.total}…`
                    : 'Submitting…'}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit for Review
                </>
              )}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
